import React, { useEffect, useMemo, useRef } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import classnames from "classnames";
import { useGlobal } from "reactn";
import { useResponsive } from "@outplayed/responsive";
import { MOBILE_SLIDE_GLOBAL_KEY } from "lib/constants";
import { sigfigFormatter } from "lib/tierHelper";
import { decimalToPercentage, getWoWTierColor } from "../../../lib/tierHelper";
import SpecLink from "./SpecLink";
import Talents from "./Talents";
import { numberWithCommas } from "lib/format_helper";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import { getFormattedRole, getFormattedEncounterType } from "../../../lib/wowHelpers";
import { getTierArrowMovedIcon } from "./TierListHelper";

const Tooltip = (props) => {
  const { text } = props;
  return (
    <TooltipContainer tooltipInfo={<div>{text}</div>}>
      <div className="tooltip-container">
        <TooltipIcon className="tooltip-icon" />
      </div>
    </TooltipContainer>
  );
};

const TierListsTable = (props) => {
  const { data, talents, role, encounterType, stat, hscombined } = props;
  const prevSort = useRef(null);
  const { currentBreakpoint } = useResponsive();
  const [mobileSlideClass] = useGlobal(MOBILE_SLIDE_GLOBAL_KEY);
  const dpsLabel = role === "tank" ? "Tank DPS" : "DPS";
  const dpsTooltip =
    role === "tank"
      ? "For Tanks we opted to show their aggregated DPS score as a temporary placeholder metric while we search for a more valuable metric that tank players care about."
      : "DPS is the aggregated Damage Per Second score that each spec does in the top rankings of Raid and Mythic+ Dungeons.";
  const populationText = encounterType === "mythic" ? "Dungeon Runs" : "Parses";
  const populationTooltip =
    encounterType === "mythic"
      ? "Dungeon Runs is directly tied to Popularity, displayed as the total number of completed dungeons by each spec in the top rankings of Mythic+ Dungeons."
      : "Parses is directly tied to Popularity, displayed as the total number of completed encounters by a spec in the top rankings of the Raid.";
  const isMobileSmall = currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM";

  const columns = useMemo(
    () =>
      data.length > 0
        ? [
            {
              Header: (
                <div className="header-text">
                  Rank
                  <Tooltip text="Rank based on selected metric. Green and Red arrows show movement of the spec's tier ranking compared to all the other specs." />
                </div>
              ),
              id: "rank",
              accessor: "arrow",
              Cell: (props) => (
                <div div style={{ position: "relative" }}>
                  <div
                    style={
                      getTierArrowMovedIcon(props.sortedRows[props.index].original.arrow_indicator) !== null // Enter the value here
                        ? { position: "absolute", left: "50%", transform: "translateX(-50%)" }
                        : { position: "absolute", left: "50%", transform: "translateX(-50%) translateY(-50%)", top: "50%" }
                    }
                  >
                    {props.index + 1}
                  </div>
                  <div style={{ marginLeft: "40px", marginTop: "-2px" }}>
                    {
                      getTierArrowMovedIcon(props.sortedRows[props.index].original.arrow_indicator) // Enter the value here
                    }
                  </div>
                </div>
              ),
              disableSortBy: true,
              minWidth: 80,
              maxWidth: 80,
            },
            {
              Header: (
                <div className="header-text">
                  Tier
                  <Tooltip text="We calculate spec tier using a combination of their aggregated DPS or HPS score and how often the spec appears in the top rankings of the Raid or Mythic+ dungeons." />
                </div>
              ),
              accessor: (d) => d,
              id: "tier",
              minWidth: 40,
              maxWidth: 90,
              sortType: (a, b) => {
                const rankingA = Number(a.original.ranking);
                const rankingB = Number(b.original.ranking);
                return rankingB - rankingA;
              },
              Cell: ({ cell: { value } }) => (
                <span className={getWoWTierColor(value.tier_letter)}>
                  <b>{value.tier_letter}</b>
                </span>
              ),
            },
            {
              Header: isMobileSmall ? "Spec." : "Specialization",
              accessor: (d) => d,
              className: "spec-column",
              minWidth: 80,
              maxWidth: 260,
              sortType: (a, b) => {
                const nameA =
                  hscombined == "1"
                    ? a.original.spec + a.original.class
                    : a.original.subspec + a.original.spec + a.original.class;
                const nameB =
                  hscombined == "1"
                    ? a.original.spec + a.original.class
                    : b.original.subspec + b.original.spec + b.original.class;
                return nameA.localeCompare(nameB);
              },
              Cell: ({ cell: { value } }) => (
                <SpecLink data={value} encounterType={encounterType} isMobileSmall={isMobileSmall} />
              ),
            },
            {
              Header: (
                <div className="header-text">
                  {dpsLabel}
                  <Tooltip text={dpsTooltip} />
                </div>
              ),
              accessor: "dps",
              id: "dps",
              minWidth: 70,
              maxWidth: 100,
              sortType: (a, b) => b.original.dps - a.original.dps,
              Cell: ({ cell: { value } }) => <span>{sigfigFormatter(value)}</span>,
            },
            {
              Header: (
                <div className="header-text">
                  HPS
                  <Tooltip text="HPS is the aggregated Healing Per Second score that each spec does in the top rankings of Raid and Mythic+ Dungeons." />
                </div>
              ),
              accessor: "hps",
              id: "hps",
              minWidth: 70,
              maxWidth: 100,
              sortType: (a, b) => b.original.hps - a.original.hps,
              Cell: ({ cell: { value } }) => <span>{sigfigFormatter(value)}</span>,
            },
            {
              Header: (
                <div className="header-text">
                  Popularity
                  <Tooltip text="Popularity is how often this spec appears in the top rankings of Raid or Mythic+ Dungeons." />
                </div>
              ),
              accessor: "pop_perc",
              minWidth: 70,
              maxWidth: 100,
              sortType: (a, b) => {
                return b.original.pop_perc - a.original.pop_perc;
              },
              Cell: ({ cell: { value } }) => <span>{decimalToPercentage(value)}</span>,
            },
            {
              Header: (
                <div className="header-text">
                  Build
                  <Tooltip text="This section highlights the final talents selected from the spec tree to give a preview of what Talent build is the most successful in a Raid or Mythic+ Dungeons." />
                </div>
              ),
              accessor: (d) => d,
              id: "build",
              disableSortBy: true,
              Cell: ({ cell: { value } }) =>
                hscombined == "1" ? (
                  <Talents data={value} talents={talents} hscombined={true} />
                ) : (
                  <Talents data={value} talents={talents} />
                ),
              minWidth: 60,
              maxWidth: 300,
            },
            {
              Header: (
                <div className="header-text">
                  {populationText}
                  <Tooltip text={populationTooltip} />
                </div>
              ),
              accessor: "count",
              id: "count",
              Cell: ({ cell: { value } }) => <span>{numberWithCommas(value)}</span>,
              minWidth: 95,
              maxWidth: 100,
              sortType: (a, b) => {
                return b.original.count - a.original.count;
              },
            },
          ]
        : [],
    [currentBreakpoint, data, isMobileSmall, stat],
  );

  const hiddenColumns = [isMobileSmall && "build", isMobileSmall && "rank", isMobileSmall && "count", role === "dps" && "hps"];

  const columnStyle = (column) => ({
    minWidth: column.totalMinWidth,
    maxWidth: column.totalMaxWidth,
    flex: `1 1 100%`,
  });

  const { getTableBodyProps, getTableProps, headerGroups, page, prepareRow, setPageSize, toggleSortBy, state } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 100,
        pageIndex: 0,
        sortBy: prevSort.current || [{ id: role == "heal" ? "hps" : "dps", desc: false }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    setPageSize(100);
  }, [currentBreakpoint]);

  useEffect(() => {
    if (state.sortBy[0].id == "dps" && role == "heal") {
      toggleSortBy("hps", false);
    } else if (state.sortBy[0].id == "hps" && role !== "heal") {
      toggleSortBy("dps", false);
    }
  }, [role]);

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(100);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div>
      <div
        {...getTableProps({
          className: "content-section ReactTable ugg-table-2 tier-list",
        })}
      >
        <div className="tier-list-text-container">
          <div className="content-section-header">
            {getFormattedRole(role)} Rankings for {getFormattedEncounterType(encounterType)}
          </div>
          <div className="description">
            We calculate each spec's {getFormattedRole(role)} rankings by evaluating DPS and HPS scores from the very best players
            and factoring in the frequency of each spec's appearances in the highest rankings of the current{" "}
            {getFormattedEncounterType(encounterType)} season. We believe this gives the most accurate rankings of every{" "}
            {getFormattedRole(role)} spec's performance at the highest levels of endgame PVE gameplay.
          </div>
        </div>
        <div className={`rt-thead -header ${mobileSlideClass}`}>
          {headerGroups.map((headerGroup, index) => (
            <div key={index} {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
              {headerGroup.headers.map((column, index) => {
                const thClassNames = classnames([
                  "rt-th",
                  column.headerClassName,
                  column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                  column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                ]);

                return (
                  <div
                    key={index}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        className: thClassNames,
                        style: columnStyle(column),
                      }),
                    )}
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps({ className: "rt-tbody" })}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell, index) => {
                    const tdClassNames = classnames("rt-td", cell.column.className, {
                      sorted: cell.column.isSorted,
                      "is-in-odd-row": i % 2 === 0,
                    });

                    return (
                      <div
                        key={index}
                        {...cell.getCellProps({
                          className: tdClassNames,
                          style: columnStyle(cell.column),
                        })}
                      >
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TierListsTable;
